import { useState, useEffect, useCallback } from 'react'
import useAuth from './useAuth'
import { getAccessToken, logout } from '../auth'

const useFetch = (url, fetchInfo, options = {}) => {
  const { loop, lazy } = typeof options === 'boolean' ? { loop: options, lazy: false } : options
  const { getAccessTokenSilently } = useAuth()
  const [loading, setLoading] = useState(!lazy)
  const [responseOk, setResponseOk] = useState(false)
  const [data, setData] = useState([])

  const fetchData = useCallback(
    async token => {
      setLoading(true)
      setResponseOk(false)

      const info = fetchInfo ? { ...fetchInfo } : { headers: {} }
      if (!info.headers) {
        info.headers = {}
      }
      if (typeof token === 'string') {
        info.headers.authorization = `Bearer ${token}`
      }
      info.credentials = 'include'

      try {
        let response = await fetch(url, info)

        if (response.status === 401 && (await getAccessToken({ force: true }))) {
          response = await fetch(url, info)
        }

        if (response.ok) {
          setResponseOk(true)
          const json = await response.json()
          setData(json)
          if (loop) {
            setTimeout(() => getAccessTokenSilently().then(fetchData).catch(console.error), 5 * 60 * 1000)
          }
        } else {
          if (response.status === 401 || response.status === 403) {
            await logout({ returnTo: window.location.href })
          }
          setData(response)
        }
      } catch (e) {
        setData(e)
      } finally {
        setLoading(false)
      }
    },
    [url, loop, getAccessTokenSilently]
  )

  const refetch = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently()
      await fetchData(token)
    } catch (e) {
      console.error(e)
      setLoading(false)
    }
  }, [getAccessTokenSilently, fetchData])

  const execute = refetch

  useEffect(() => {
    if (lazy) return

    getAccessTokenSilently()
      .then(token => fetchData(token))
      .catch(e => {
        console.error(e)
        setLoading(false)
      })
  }, [])

  return { loading, data, responseOk, refetch, execute }
}

export default useFetch
